import { ReactNode, useEffect, useState } from "react";
import { classificationLabel, reasonLabel, ResearchOutcome } from "./researchResult";

type Filters = { query: string; country: string; type: string; reason: string; fit: string; confidence: string };
type Sort = "original" | "name" | "fit" | "confidence";
const EMPTY: Filters = { query: "", country: "", type: "", reason: "", fit: "", confidence: "" };
const inputClass = "rounded border border-border bg-background px-3 py-2 text-sm";
const normalize = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

function fields(row: ResearchOutcome) {
  const e = row.kind === "evaluation" ? row.evaluation : null;
  return {
    name: e?.organization.name ?? row.organizationId,
    country: e?.organization.country ?? "",
    city: e?.organization.city ?? "",
    type: e?.organization.type ?? "",
    fit: e?.fit_score ?? null,
    confidence: e?.confidence ?? null,
    reasons: e?.reason_codes ?? [row.kind === "contract_rejection" ? row.rejection.code : ""],
    sources: e?.sources.length ?? null,
    website: e?.organization.website ?? "",
  };
}

/** Filters apply to the current authorized result; they never alter its summary. */
export function browseResearch(rows: ResearchOutcome[], filters: Filters, sort: Sort) {
  const query = normalize(filters.query.trim());
  const result = rows.filter(row => {
    const f = fields(row);
    const search = [f.name, row.organizationId, f.country, f.city, f.type, f.website,
      classificationLabel(row.classification), ...f.reasons.map(reasonLabel)].join(" ");
    return (!query || normalize(search).includes(query)) &&
      (!filters.country || f.country === filters.country) &&
      (!filters.type || f.type === filters.type) &&
      (!filters.reason || f.reasons.includes(filters.reason)) &&
      (!filters.fit || (f.fit !== null && f.fit >= Number(filters.fit))) &&
      (!filters.confidence || (f.confidence !== null && f.confidence >= Number(filters.confidence)));
  });
  if (sort !== "original") result.sort((a, b) => {
    const left = fields(a), right = fields(b);
    if (sort === "name") return left.name.localeCompare(right.name, "hr");
    // Contract rejections have no score; always put them last rather than inventing 0.
    return (right[sort] ?? -1) - (left[sort] ?? -1);
  });
  return result;
}

export function ResearchBrowser({ outcomes, filteredOutcomes, renderDetail, onResetStatus, filterKey = "all" }: {
  outcomes: ResearchOutcome[];
  filteredOutcomes: ResearchOutcome[];
  renderDetail: (outcome: ResearchOutcome, index: number) => ReactNode;
  onResetStatus: () => void;
  filterKey?: string;
}) {
  const [view, setView] = useState<"cards" | "table">("cards");
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [sort, setSort] = useState<Sort>("original");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [expanded, setExpanded] = useState<string | null>(null);
  useEffect(() => { setPage(0); setExpanded(null); }, [filterKey]);
  const visible = browseResearch(filteredOutcomes, filters, sort);
  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize));
  // Status buttons live in the parent: clamp immediately, including its first render.
  const currentPage = Math.min(page, pageCount - 1);
  const start = currentPage * pageSize;
  const pageRows = visible.slice(start, start + pageSize);
  const options = (key: "country" | "type" | "reason") => Array.from(new Set(
    outcomes.flatMap(row => key === "reason" ? fields(row).reasons : [fields(row)[key]])
  )).filter(Boolean).sort((a, b) => a.localeCompare(b, "hr"));
  const update = (key: keyof Filters, value: string) => {
    setFilters(previous => ({ ...previous, [key]: value })); setPage(0); setExpanded(null);
  };
  const reset = () => {
    setFilters(EMPTY); setSort("original"); setPage(0); setExpanded(null); onResetStatus();
  };

  return <div className="space-y-3">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="inline-flex rounded border border-border p-1" aria-label="Način prikaza">
        {(["cards", "table"] as const).map(mode => <button key={mode} type="button"
          aria-pressed={view === mode} data-testid={`research-view-${mode}`}
          onClick={() => { setView(mode); setExpanded(null); }}
          className={`rounded px-3 py-1.5 text-sm ${view === mode ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
          {mode === "cards" ? "Kartice" : "Tablica"}
        </button>)}
      </div>
      <p className="text-sm text-muted-foreground" role="status" data-testid="research-match-count">
        {visible.length} od {outcomes.length} zapisa u ovom rezultatu
      </p>
    </div>

    <div className="rounded border border-border p-3 space-y-3">
      <label className="block text-xs text-muted-foreground">Pretraži ovaj rezultat
        <input type="search" value={filters.query} onChange={e => update("query", e.target.value)}
          placeholder="Naziv, ID, grad, web ili razlog…" data-testid="research-search"
          className={`${inputClass} mt-1 w-full text-foreground`} />
      </label>
      <div className="flex flex-wrap items-end gap-3">
        {([['country', 'Država'], ['type', 'Tip'], ['reason', 'Razlog']] as const).map(([key, label]) =>
          <label key={key} className="text-xs text-muted-foreground">{label}
            <select value={filters[key]} onChange={e => update(key, e.target.value)}
              data-testid={`research-select-${key}`} className={`${inputClass} mt-1 block text-foreground`}>
              <option value="">{key === "reason" ? "Svi razlozi" : key === "country" ? "Sve države" : "Svi tipovi"}</option>
              {options(key).map(value => <option key={value} value={value}>{key === "reason" ? reasonLabel(value) : value}</option>)}
            </select>
          </label>)}
        {([['fit', 'Min. fit'], ['confidence', 'Min. pouzdanost']] as const).map(([key, label]) =>
          <label key={key} className="text-xs text-muted-foreground">{label}
            <select value={filters[key]} onChange={e => update(key, e.target.value)}
              data-testid={`research-min-${key}`} className={`${inputClass} mt-1 block text-foreground`}>
              <option value="">Bez minimuma</option>
              {[0, 25, 50, 75, 100].map(value => <option key={value} value={value}>{value}</option>)}
            </select>
          </label>)}
        <label className="text-xs text-muted-foreground">Poredak
          <select value={sort} onChange={e => { setSort(e.target.value as Sort); setPage(0); }}
            data-testid="research-sort" className={`${inputClass} mt-1 block text-foreground`}>
            <option value="original">Izvorni redoslijed</option><option value="name">Naziv A–Ž</option>
            <option value="fit">Fit: viši prvo</option><option value="confidence">Pouzdanost: viša prvo</option>
          </select>
        </label>
        <button type="button" onClick={reset} data-testid="research-reset"
          className="rounded border border-border px-3 py-2 text-sm">Poništi filtre</button>
      </div>
    </div>

    {visible.length === 0 ? <p className="rounded border border-border p-4 text-sm text-muted-foreground"
      data-testid="research-no-rows">Nema zapisa za odabrane filtre.</p> : view === "cards" ?
      <ul className="space-y-3" data-testid="research-outcomes">
        {pageRows.map((row, index) => <DetailItem key={`${row.kind}-${row.organizationId}`}>
          {renderDetail(row, start + index)}
        </DetailItem>)}
      </ul> : <div className="max-h-[65vh] overflow-auto rounded border border-border"
        role="region" aria-label="Tablični rezultati istraživanja" tabIndex={0}>
        <table className="w-full min-w-[980px] text-left text-sm" data-testid="research-table">
          <caption className="sr-only">Rezultati istraživanja; detalji i izvori otvaraju se po zapisu.</caption>
          <thead className="sticky top-0 z-10 bg-muted text-xs text-muted-foreground">
            <tr>{["Organizacija", "Status", "Tip", "Država / grad", "Fit", "Pouzdanost", "Razlozi", "Izvori", ""].map((heading, i) =>
              <th key={i} scope="col" className="px-3 py-3 font-medium">{heading || <span className="sr-only">Detalji</span>}</th>)}</tr>
          </thead>
          <tbody>{pageRows.map((row, index) => {
            const f = fields(row), key = `${row.kind}-${row.organizationId}`;
            return <TableItem key={key} row={row} expanded={expanded === key}
              toggle={() => setExpanded(expanded === key ? null : key)} detail={renderDetail(row, start + index)}
              cells={[f.name, classificationLabel(row.classification), f.type || "—",
                [f.country, f.city].filter(Boolean).join(" / ") || "—", f.fit ?? "—", f.confidence ?? "—",
                f.reasons.map(reasonLabel).join(", "), f.sources ?? "—"]} />;
          })}</tbody>
        </table>
      </div>}

    {visible.length > 0 && <nav className="flex flex-wrap items-center justify-between gap-3 text-sm"
      aria-label="Stranice rezultata">
      <label className="text-muted-foreground">Redaka po stranici <select value={pageSize}
        data-testid="research-page-size" className={inputClass}
        onChange={e => { setPageSize(Number(e.target.value)); setPage(0); setExpanded(null); }}>
        {[25, 50, 100].map(size => <option key={size} value={size}>{size}</option>)}
      </select></label>
      <div className="flex items-center gap-3">
        <span data-testid="research-page-info">{start + 1}–{Math.min(start + pageSize, visible.length)} od {visible.length} · Stranica {currentPage + 1}/{pageCount}</span>
        <button type="button" disabled={currentPage === 0} data-testid="research-page-prev"
          onClick={() => { setPage(currentPage - 1); setExpanded(null); }} className="rounded border border-border px-3 py-2 disabled:opacity-40">Prethodna</button>
        <button type="button" disabled={currentPage + 1 >= pageCount} data-testid="research-page-next"
          onClick={() => { setPage(currentPage + 1); setExpanded(null); }} className="rounded border border-border px-3 py-2 disabled:opacity-40">Sljedeća</button>
      </div>
    </nav>}
  </div>;
}

// A fragment keeps the existing card's native li semantics and test IDs.
function DetailItem({ children }: { children: ReactNode }) { return <>{children}</>; }

function TableItem({ row, cells, expanded, toggle, detail }: {
  row: ResearchOutcome; cells: (string | number)[]; expanded: boolean; toggle: () => void; detail: ReactNode;
}) {
  return <>
    <tr className="border-t border-border hover:bg-muted/30" data-testid="research-table-row"
      data-classification={row.classification}>
      {cells.map((cell, index) => <td key={index} className={`px-3 py-3 align-top ${index === 0 ? "min-w-[200px] font-medium" : index === 6 ? "max-w-[240px]" : ""}`}>
        {cell}{index === 0 && <div className="mt-1 font-mono text-xs text-muted-foreground">{row.organizationId}</div>}
      </td>)}
      <td className="px-3 py-3 align-top"><button type="button" onClick={toggle} aria-expanded={expanded}
        aria-label={`${expanded ? "Zatvori" : "Otvori"} detalje: ${cells[0]}`}
        className="rounded border border-border px-3 py-1.5 text-xs whitespace-nowrap">
        {expanded ? "Zatvori" : "Detalji"}</button></td>
    </tr>
    {expanded && <tr className="border-t border-border bg-muted/20"><td colSpan={9} className="p-4">
      <ul className="list-none">{detail}</ul>
    </td></tr>}
  </>;
}
